// 📊 Servicios API para Facturas Internas
// Integración con backend según INTERNAL_INVOICE_FRONTEND_GUIDE.md
import type {
  GenerateInternalInvoiceRequest,
  BatchGenerateInternalInvoiceRequest,
  InternalInvoiceGenerationResultDto,
  BatchGenerateInternalInvoiceResponse,
  InternalInvoiceGenerationDetails,
  InternalInvoiceRatePreviewDto,
  RateDecision,
  RateDecisionError,
} from "@/types/internal-invoices";
import { appConfig } from "@/config/app-config";
/**
 * Obtiene el token JWT del almacenamiento OIDC
 * react-oidc-context guarda el usuario en sessionStorage con key:
 * oidc.user:{authority}:{client_id}
 */
function getAuthToken(): string | null {
  try {
    // Key donde react-oidc-context guarda el usuario
    const oidcKey = `oidc.user:${appConfig.oidc.authority}:${appConfig.oidc.clientId}`;
    const oidcData = sessionStorage.getItem(oidcKey);
    if (oidcData) {
      const parsed = JSON.parse(oidcData);
      return parsed.access_token || null;
    }
  } catch (error) {
  }
  return null;
}
/**
 * Headers comunes para las peticiones con autenticación
 */

function pick<T = any>(raw: any, camel: string, pascal: string): T {
  return raw?.[camel] ?? raw?.[pascal];
}

function normalizeNumber(value: any): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === "object" && "parsedValue" in value) return Number(value.parsedValue);
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizeRateDecision(raw: any): RateDecision | null {
  if (!raw) return null;
  return {
    mode: pick(raw, "mode", "Mode"),
    fromCurrencyId: pick(raw, "fromCurrencyId", "FromCurrencyId") ?? 0,
    toCurrencyId: pick(raw, "toCurrencyId", "ToCurrencyId") ?? 0,
    carrierInvoiceDate: pick(raw, "carrierInvoiceDate", "CarrierInvoiceDate"),
    requestedRateDate: pick(raw, "requestedRateDate", "RequestedRateDate"),
    effectiveRateDate: pick(raw, "effectiveRateDate", "EffectiveRateDate") ?? null,
    baseRate: normalizeNumber(pick(raw, "baseRate", "BaseRate")),
    markupPercent: normalizeNumber(pick(raw, "markupPercent", "MarkupPercent")) ?? 0,
    finalRate: normalizeNumber(pick(raw, "finalRate", "FinalRate")) ?? 0,
    rateSource: pick(raw, "rateSource", "RateSource") ?? null,
    policyUsed: pick(raw, "policyUsed", "PolicyUsed") ?? null,
    isStale: Boolean(pick(raw, "isStale", "IsStale")),
    manualRateUserId: pick(raw, "manualRateUserId", "ManualRateUserId") ?? null,
    manualRateReason: pick(raw, "manualRateReason", "ManualRateReason") ?? null,
  };
}

function normalizeRateDecisionError(raw: any): RateDecisionError | null {
  if (!raw) return null;
  return {
    errorCode: pick(raw, "errorCode", "ErrorCode") ?? "RATE_DECISION_ERROR",
    message: pick(raw, "message", "Message") ?? "No se pudo calcular el rate automatico.",
    fromCurrencyId: pick(raw, "fromCurrencyId", "FromCurrencyId") ?? 0,
    toCurrencyId: pick(raw, "toCurrencyId", "ToCurrencyId") ?? 0,
    requestedRateDate: pick(raw, "requestedRateDate", "RequestedRateDate"),
    allowLegacyFallback: Boolean(pick(raw, "allowLegacyFallback", "AllowLegacyFallback")),
    forceLegacyFallback: Boolean(pick(raw, "forceLegacyFallback", "ForceLegacyFallback")),
  };
}

function normalizeRatePreview(raw: any): InternalInvoiceRatePreviewDto {
  return {
    success: Boolean(pick(raw, "success", "Success")),
    carrierInvoiceId: pick(raw, "carrierInvoiceId", "CarrierInvoiceId"),
    rateDecision: normalizeRateDecision(pick(raw, "rateDecision", "RateDecision")),
    error: normalizeRateDecisionError(pick(raw, "error", "Error")),
  };
}

function getAuthHeaders(): HeadersInit {
  const token = getAuthToken();
  const headers: HeadersInit = {};
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  return headers;
}

/**
 * Previsualiza el rate automatico calculado por backend antes de generar individualmente.
 */
export async function previewInternalInvoiceRate(
  carrierInvoiceId: number
): Promise<InternalInvoiceRatePreviewDto> {
  const response = await fetch(appConfig.getFinanceUrl("finance/internal-invoices/generate-mirror/rate-preview"), {
    method: "POST",
    headers: {
      ...getAuthHeaders(),
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ carrierInvoiceId }),
  });

  const rawText = await response.text();
  const raw = rawText ? JSON.parse(rawText) : null;

  if (!response.ok) {
    throw new Error(raw?.message || raw?.error || `Error ${response.status}: ${response.statusText}`);
  }

  return normalizeRatePreview(raw);
}

/**
 * Genera una factura interna individual a partir de una factura de proveedor
 *
 * @param request - Datos para generar la factura interna
 * @returns Resultado de la generación con detalles completos
 */
export async function generateInternalInvoice(
  request: GenerateInternalInvoiceRequest
): Promise<InternalInvoiceGenerationResultDto> {
  try {
    const response = await fetch(appConfig.getFinanceUrl("finance/internal-invoices/generate-mirror"), {
      method: "POST",
      headers: {
        ...getAuthHeaders(),
        "Content-Type": "application/json",
      },
      body: JSON.stringify(request),
    });
    if (!response.ok) {
      const errorText = await response.text();
      // Intentar parsear como JSON si es posible
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { error: errorText || response.statusText };
      }
      return {
        success: false,
        error: errorData.error || `Error ${response.status}: ${response.statusText}`,
        internalInvoiceId: null,
        linesCreated: 0,
        totalAmount: 0,
        details: {
          extraLinesProcessed: 0,
          fleteLinesProcessed: 0,
          fobLinesProcessed: 0,
          linesSkippedNoMapping: 0,
          linesSkippedNoRate: 0,
          equipmentTypesProcessed: 0,
        },
        warnings: [],
    };
  }
  const data: InternalInvoiceGenerationResultDto = await response.json();
  return data;
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : "Error de conexión con el servidor",
      internalInvoiceId: null,
      linesCreated: 0,
      totalAmount: 0,
      details: {
        extraLinesProcessed: 0,
        fleteLinesProcessed: 0,
        fobLinesProcessed: 0,
        linesSkippedNoMapping: 0,
        linesSkippedNoRate: 0,
        equipmentTypesProcessed: 0,
      },
      warnings: [],
    };
  }
}

/**
 * Obtiene los detalles de generación (tasas de cambio usadas, config) desde blob storage
 *
 * @param internalInvoiceId - ID de la factura interna
 * @returns Registro JSON con conversiones, fuentes de tasa y flags de config
 */
export async function getGenerationDetails(
  internalInvoiceId: number,
  token: string | null
): Promise<InternalInvoiceGenerationDetails | null> {
  const response = await fetch(
    appConfig.getFinanceUrl(`finance/internal-invoices/${internalInvoiceId}/generation-details`),
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  if (response.status === 404) return null;
  if (!response.ok) {
    throw new Error(`[getGenerationDetails] HTTP ${response.status}: ${response.statusText}`);
  }
  const raw = await response.json();
  return normalizeGenerationDetails(raw);
}

// Normaliza respuesta PascalCase del backend a camelCase
function normalizeGenerationDetails(raw: any): InternalInvoiceGenerationDetails {
  return {
    internalInvoiceId: pick(raw, "internalInvoiceId", "InternalInvoiceId"),
    carrierInvoiceId: pick(raw, "carrierInvoiceId", "CarrierInvoiceId"),
    generatedAt: pick(raw, "generatedAt", "GeneratedAt"),
    targetCurrencyId: pick(raw, "targetCurrencyId", "TargetCurrencyId"),
    targetCurrencyCode: pick(raw, "targetCurrencyCode", "TargetCurrencyCode"),
    linesCreated: pick(raw, "linesCreated", "LinesCreated") ?? 0,
    totalAmount: normalizeNumber(pick(raw, "totalAmount", "TotalAmount")) ?? 0,
    rateDecision: normalizeRateDecision(pick(raw, "rateDecision", "RateDecision")),
    conversions: (pick<any[]>(raw, "conversions", "Conversions") || []).map((c: any) => ({
      fromCurrencyId: pick(c, "fromCurrencyId", "FromCurrencyId"),
      toCurrencyId: pick(c, "toCurrencyId", "ToCurrencyId"),
      requestedRateDate: pick(c, "requestedRateDate", "RequestedRateDate") ?? null,
      effectiveRateDate: pick(c, "effectiveRateDate", "EffectiveRateDate") ?? null,
      baseRate: normalizeNumber(pick(c, "baseRate", "BaseRate")),
      markupPercent: normalizeNumber(pick(c, "markupPercent", "MarkupPercent")),
      finalRate: normalizeNumber(pick(c, "finalRate", "FinalRate")),
      rate: normalizeNumber(pick(c, "rate", "Rate")) ?? normalizeNumber(pick(c, "finalRate", "FinalRate")) ?? 0,
      rateDate: pick(c, "rateDate", "RateDate") ?? pick(c, "effectiveRateDate", "EffectiveRateDate"),
      rateSource: pick(c, "rateSource", "RateSource") ?? null,
      policyUsed: pick(c, "policyUsed", "PolicyUsed") ?? null,
      source: pick(c, "source", "Source") ?? pick(c, "policyUsed", "PolicyUsed") ?? pick(c, "rateSource", "RateSource"),
      isStale: Boolean(pick(c, "isStale", "IsStale")),
    })),
    config: {
      rateValidationMode: pick(raw.Config ?? raw.config, "rateValidationMode", "RateValidationMode"),
      maxAgeHours: pick(raw.Config ?? raw.config, "maxAgeHours", "MaxAgeHours"),
      allowLegacyFallback: Boolean(pick(raw.Config ?? raw.config, "allowLegacyFallback", "AllowLegacyFallback")),
      forceLegacyFallback: Boolean(pick(raw.Config ?? raw.config, "forceLegacyFallback", "ForceLegacyFallback")),
    },
  };
}

/**
 * Genera facturas internas en lote a partir de múltiples facturas de proveedor
 *
 * @param request - Array de IDs de facturas de proveedor
 * @returns Resultado del lote con estadísticas por factura
 */
export async function generateInternalInvoicesBatch(
  request: BatchGenerateInternalInvoiceRequest
): Promise<BatchGenerateInternalInvoiceResponse> {
  try {
    const response = await fetch(appConfig.getFinanceUrl("finance/internal-invoices/generate-batch"), {
      method: "POST",
      headers: {
        ...getAuthHeaders(),
        "Content-Type": "application/json",
      },
      body: JSON.stringify(request),
    });
    if (!response.ok) {
      const errorText = await response.text();
      // Para lote, devolver estructura de error
      return {
        totalRequested: request.carrierInvoiceIds.length,
        totalSuccess: 0,
        totalFailed: request.carrierInvoiceIds.length,
        results: request.carrierInvoiceIds.map(id => ({
          carrierInvoiceId: id,
          success: false,
          internalInvoiceId: null,
          linesCreated: 0,
          totalAmount: 0,
          error: errorText || response.statusText,
          warnings: [],
        })),
      };
    }
    const data: BatchGenerateInternalInvoiceResponse = await response.json();
    return data;
  } catch (error) {
    // Para lote, devolver estructura de error
    return {
      totalRequested: request.carrierInvoiceIds.length,
      totalSuccess: 0,
      totalFailed: request.carrierInvoiceIds.length,
      results: request.carrierInvoiceIds.map(id => ({
        carrierInvoiceId: id,
        success: false,
        internalInvoiceId: null,
        linesCreated: 0,
        totalAmount: 0,
        error: error instanceof Error ? error.message : "Error de conexión con el servidor",
        warnings: [],
      })),
    };
  }
}
